import { Injectable } from '@angular/core';
import { SpeechEngine } from '@app/event/speech-engines/AbstractSpeechEngine';
import { bindCallback, combineLatest, forkJoin, from, Observable, Subject, throwError } from 'rxjs';
import { RoutingService } from '@app/routing.service';
import { Socket } from 'ngx-socket-io';
import {
  catchError,
  concatMap, delay,
  finalize,
  map,
  mergeMap,
  switchMap,
  tap
} from 'rxjs/operators';
import RecordRTC from 'recordrtc';
import { UserService } from '@app/user/user.service';
import { ElectronService } from 'ngx-electron';
import { Select } from '@ngxs/store';
import {
  InputDevice,
  SettingsState
} from '@app/shared/settings/settings.state';
import { DebugService } from '@app/services/debug.service';
import * as stt from 'watson-speech/dist/watson-speech';
import * as MicrophoneStream from 'microphone-stream';
import { data } from 'autoprefixer';
import * as bufferFrom from 'buffer-from';

@Injectable({
  providedIn: 'root'
})
export class GoogleService implements SpeechEngine {
  stream;
  inverval;
  recordRTC;
  bufferUnusedSamples: any = [];
  public bypass: boolean = false;
  public suppressFinalization: boolean = false;

  @Select(SettingsState.defaultInput) defaultInput: Observable<InputDevice>;
  @Select(SettingsState.defaultInput) defaultDesktopInput: Observable<InputDevice>;
  private recorder;
  private source: MediaStreamAudioSourceNode;

  constructor(
    private socket: Socket,
    private userService: UserService,
    private electronService: ElectronService
    // private debug: DebugService,
  ) {
  }

  startListening$(eventId: string): Observable<any> {
    let streamSnapshot;
    // this.debug.resetAudioBuffer();
    return combineLatest([this.defaultInput, this.defaultDesktopInput]).pipe(
      map(([input, defaultDesktopInput]) => {
        const desktopConstraints = {
          // @ts-ignore
          audio: {
            // @ts-ignore
            mandatory: {
              chromeMediaSource: 'desktop'
            }
          },
          video: {
            // @ts-ignore
            mandatory: {
              chromeMediaSource: 'desktop'
            }
          }
        };


        if (defaultDesktopInput.id) {
          // @ts-ignore
          desktopConstraints.audio.mandatory.chromeMediaSourceId = defaultDesktopInput.id;
        }

        const audioSettings = {
          echoCancellation: 'false'
        };

        const webConstraints = {
          audio: input.id ? { deviceId: input.id, ...audioSettings } : true
        };

        const isDesktopApp = this.electronService.isElectronApp;


        if (isDesktopApp && defaultDesktopInput.id) {
          // @ts-ignore
          const microphone = navigator.mediaDevices.getUserMedia(webConstraints);

          return [microphone];
        } else {
          // @ts-ignore
          const microphone = navigator.mediaDevices.getUserMedia(webConstraints);
          return [microphone];
        }
      }),
      switchMap(inputs => combineLatest(inputs)),
      catchError(error => {
        alert("Could not start audio device.")
        return throwError(error);
      }),
      map((streams, streamsDesktop) => {
        console.log(streamsDesktop);
        let stream;

        const audioContext = new AudioContext();
        if (streams.length >= 2) {
          // const dest = audioContext.createMediaStreamDestination();



          streams.map(streami => {


            // source.connect(dest);
          });
          // stream = dest.stream;
        } else {
          stream = streams[0];
          streamSnapshot = stream;

           this.source = audioContext.createMediaStreamSource(stream);


          this.recorder =
            audioContext.createScriptProcessor(null, 1, 1)


          const recorderProcess = (e: AudioProcessingEvent) => {
            // onaudioprocess can be called at least once after we've stopped

            const buffer = e.inputBuffer.getChannelData(0).buffer

            const flt32ar = new Float32Array(buffer);
            
            if (this.bypass)
              flt32ar.fill(0);

            const res = this.downsample(flt32ar, e.inputBuffer.sampleRate)

            const dataaa = this.floatTo16BitPCM(res);

            this.socket.ioSocket.binary(true).compress(false).emit('transcription.rawPCMData',
              { payload: dataaa, shortId: eventId, userId: this.userService._currentUser.id, ts: Date.now() }, (ack) => {
                // data = null;
                ack = null;
              }
            );
          };

          this.recorder.onaudioprocess = recorderProcess


          this.source.connect(this.recorder);
          this.recorder.connect(audioContext.destination);
        }


        return stream;
      }),
      finalize(() => {
        console.log('stopping recording...');
        // clearInterval(this.inverval);
        this.recorder.disconnect();
        this.source.disconnect();
        this.recorder = null;
        this.source = null;
        // this.debug.playbackAudio();
        // @ts-ignore
        if (streamSnapshot && streamSnapshot.getTracks) {
          const tracks = streamSnapshot.getTracks();
          tracks.map(track => track.stop());
        }

        this.socket.emit('transcription.onStop', { suppressFinalization: this.suppressFinalization });

        if (this.suppressFinalization)
          this.suppressFinalization = false;
      })
    );
  }

  downsample(bufferInput, soureSampleRate){
    const bufferNewSamples = new Float32Array(bufferInput);

    let buffer = null;
    const newSamples = bufferNewSamples.length;
    const unusedSamples = this.bufferUnusedSamples.length;
    let i;
    let offset;

    if (unusedSamples > 0) {
      buffer = new Float32Array(unusedSamples + newSamples);
      for (i = 0; i < unusedSamples; ++i) {
        buffer[i] = this.bufferUnusedSamples[i];
      }
      for (i = 0; i < newSamples; ++i) {
        buffer[unusedSamples + i] = bufferNewSamples[i];
      }
    } else {
      buffer = bufferNewSamples;
    }

    // Downsampling and low-pass filter:
    // Input audio is typically 44.1kHz or 48kHz, this downsamples it to 16kHz.
    // It uses a FIR (finite impulse response) Filter to remove (or, at least attinuate)
    // audio frequencies > ~8kHz because sampled audio cannot accurately represent
    // frequiencies greater than half of the sample rate.
    // (Human voice tops out at < 4kHz, so nothing important is lost for transcription.)
    // See http://dsp.stackexchange.com/a/37475/26392 for a good explination of this code.
    const filter = [
      -0.037935,
      -0.00089024,
      0.040173,
      0.019989,
      0.0047792,
      -0.058675,
      -0.056487,
      -0.0040653,
      0.14527,
      0.26927,
      0.33913,
      0.26927,
      0.14527,
      -0.0040653,
      -0.056487,
      -0.058675,
      0.0047792,
      0.019989,
      0.040173,
      -0.00089024,
      -0.037935
    ];
    const samplingRateRatio = soureSampleRate / 16_000;
    const nOutputSamples = Math.floor((buffer.length - filter.length) / samplingRateRatio) + 1;
    const outputBuffer = new Float32Array(nOutputSamples);

    for (i = 0; i < outputBuffer.length; i++) {
      offset = Math.round(samplingRateRatio * i);
      let sample = 0;
      for (let j = 0; j < filter.length; ++j) {
        sample += buffer[offset + j] * filter[j];
      }
      outputBuffer[i] = sample;
    }

    const indexSampleAfterLastUsed = Math.round(samplingRateRatio * i);
    const remaining = buffer.length - indexSampleAfterLastUsed;
    if (remaining > 0) {
      this.bufferUnusedSamples = new Float32Array(remaining);
      for (i = 0; i < remaining; ++i) {
        this.bufferUnusedSamples[i] = buffer[indexSampleAfterLastUsed + i];
      }
    } else {
      this.bufferUnusedSamples = new Float32Array(0);
    }

    return outputBuffer;
  }

  floatTo16BitPCM(input: any){
    const output = new DataView(new ArrayBuffer(input.length * 2)); // length is in bytes (8-bit), so *2 to get 16-bit length
    for (let i = 0; i < input.length; i++) {
      const multiplier = input[i] < 0 ? 0x8000 : 0x7fff; // 16-bit signed range is -32768 to 32767
      output.setInt16(i * 2, (input[i] * multiplier) | 0, true); // index, value ("| 0" = convert to 32-bit int, round towards 0), littleEndian.
    }
    return bufferFrom(output.buffer);
  }


}

